import React from 'react';
import { Chip, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import { CaretDownOutlined, ExperimentOutlined } from '@ant-design/icons';
import FullScreen from 'layout/Dashboard/Header/HeaderContent/FullScreen';
import useDemoMode from 'hooks/useDemoMode';
import useAccountScope from 'hooks/useAccountScope';
import useIbkrBrokerLiveState from 'hooks/useIbkrBrokerLiveState';
import useIbkrImportOperations from 'hooks/useIbkrImportOperations';
import { DATA_INVALIDATION_EVENT } from 'utils/dataInvalidation';

export default function HeaderUtilities() {
  const { demoMode, setDemoMode, showDemoAccount, isLoaded } = useDemoMode();
  const { scope, setScope } = useAccountScope();
  const brokerLive = useIbkrBrokerLiveState();
  const brokerImports = useIbkrImportOperations();
  const [saving, setSaving] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const refreshAccounts = (event) => {
      if (!event.detail?.resources?.includes('accounts')) return;
      brokerLive.reload?.();
      brokerImports.reload?.();
    };
    window.addEventListener(DATA_INVALIDATION_EVENT, refreshAccounts);
    return () => window.removeEventListener(DATA_INVALIDATION_EVENT, refreshAccounts);
  }, [brokerImports.reload, brokerLive.reload]);

  const accounts = React.useMemo(() => {
    const byId = new Map();
    brokerLive.connection?.account_ids?.forEach((accountId) => byId.set(String(accountId), { accountId: String(accountId), broker: 'IBKR', source: 'Live' }));
    brokerImports.imports?.forEach((item) => {
      if (!item.account_id) return;
      const accountId = String(item.account_id);
      const current = byId.get(accountId);
      byId.set(accountId, { accountId, broker: item.broker || current?.broker || 'Broker', source: current ? 'Live + CSV' : 'CSV' });
    });
    return Array.from(byId.values());
  }, [brokerImports.imports, brokerLive.connection?.account_ids]);

  const demoOnly = accounts.length === 0;
  const currentLabel = demoOnly || demoMode || scope.type === 'demo' ? 'DEMO' : scope.type === 'account' ? scope.label : 'All accounts';

  React.useEffect(() => {
    if (!brokerLive.hasLoaded || !brokerImports.importsLoaded || !demoOnly || demoMode) return;
    setScope({ type: 'demo', accountId: null, label: 'Demo' });
    setDemoMode(true);
  }, [brokerImports.importsLoaded, brokerLive.hasLoaded, demoMode, demoOnly, setDemoMode, setScope]);

  const selectScope = async (nextScope) => {
    if (!isLoaded || saving) return;
    setSaving(true);
    try {
      setScope(nextScope);
      await setDemoMode(nextScope.type === 'demo');
      setAnchorEl(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Tooltip title={demoOnly ? 'Only demo data is available until a broker account is connected or imported.' : 'Choose the accounts used by dashboard data.'}>
        <Chip
          icon={<ExperimentOutlined />}
          deleteIcon={<CaretDownOutlined />}
          onDelete={demoOnly ? undefined : (event) => setAnchorEl(event.currentTarget)}
          label={currentLabel}
          clickable
          disabled={!isLoaded || saving || demoOnly}
          onClick={demoOnly ? undefined : (event) => setAnchorEl(event.currentTarget)}
          sx={{ height: 28, bgcolor: demoMode ? 'var(--app-warning-surface)' : 'var(--app-surface-subtle)', color: demoMode ? '#e0a325' : 'var(--app-text-secondary)', border: '1px solid', borderColor: demoMode ? 'var(--app-warning-border)' : 'var(--app-border)', fontSize: 11, fontWeight: 700, '& .MuiChip-icon': { color: 'inherit' } }}
        />
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {(demoOnly || showDemoAccount) ? <MenuItem selected={demoMode || scope.type === 'demo'} onClick={() => selectScope({ type: 'demo', accountId: null, label: 'Demo' })}>Demo</MenuItem> : null}
        <MenuItem selected={!demoMode && scope.type === 'all'} onClick={() => selectScope({ type: 'all', accountId: null, label: 'All accounts' })}>All accounts</MenuItem>
        {accounts.map((account) => {
          const label = `${account.broker} · ${account.accountId}`;
          return <MenuItem key={`${account.broker}-${account.accountId}`} selected={!demoMode && scope.type === 'account' && scope.accountId === account.accountId} onClick={() => selectScope({ type: 'account', accountId: account.accountId, label })}>{label} ({account.source})</MenuItem>;
        })}
      </Menu>
      <BoxedFullScreen />
    </Stack>
  );
}

function BoxedFullScreen() {
  return <Stack sx={{ '& .MuiIconButton-root': { width: 36, height: 36, border: '1px solid var(--app-border)', borderRadius: '10px', color: 'var(--app-text-secondary)', '&:hover': { bgcolor: 'var(--app-hover)' } } }}><FullScreen /></Stack>;
}
